import React from "react";
import { motion } from "framer-motion";

/**
 * Use this for screenshots that ARE plain app captures (no built-in phone bezel).
 * Wraps them in a premium iPhone 15 Pro–style frame.
 */
const SIZES = {
    sm: "w-[210px] h-[440px] md:w-[230px] md:h-[480px] rounded-[2.2rem] border-[8px]",
    md: "w-[250px] h-[520px] md:w-[280px] md:h-[580px] rounded-[2.6rem] border-[10px]",
    lg: "w-[280px] h-[580px] md:w-[320px] md:h-[660px] rounded-[3rem] border-[12px]",
};

export const PhoneMockup = ({
    src,
    alt = "Voro app screenshot",
    size = "md",
    rotate = 0,
    floatDelay = 0,
    glow = true,
    className = "",
    testId,
}) => {
    const sizeClasses = SIZES[size] || SIZES.md;

    const inner = (
        <div
            data-testid={testId}
            className={`relative ${sizeClasses} border-[#0a0a0d] bg-[#050505] iphone-shadow overflow-hidden ring-1 ring-white/10`}
            style={{ transform: `rotate(${rotate}deg)` }}
        >
            <div className="absolute -left-[3px] top-24 h-12 w-[3px] rounded-l bg-zinc-700/80" />
            <div className="absolute -left-[3px] top-40 h-16 w-[3px] rounded-l bg-zinc-700/80" />
            <div className="absolute -right-[3px] top-28 h-20 w-[3px] rounded-r bg-zinc-700/80" />
            <div className="absolute top-2 left-1/2 -translate-x-1/2 h-[26px] w-[100px] rounded-full bg-black z-20 border border-zinc-900/80" />
            <img
                src={src}
                alt={alt}
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover object-top select-none"
                loading="lazy"
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-transparent via-white/[0.04] to-transparent" />
        </div>
    );

    return (
        <div className={`relative ${className}`}>
            {glow && (
                <div
                    className="absolute inset-0 -z-10 blur-3xl opacity-50"
                    style={{
                        background:
                            "radial-gradient(closest-side, rgba(139,92,246,0.55), transparent 70%)",
                    }}
                />
            )}
            <motion.div
                initial={{ y: 0 }}
                animate={{ y: [-12, 12, -12] }}
                transition={{
                    repeat: Infinity,
                    duration: 7 + floatDelay,
                    ease: "easeInOut",
                    delay: floatDelay,
                }}
            >
                {inner}
            </motion.div>
        </div>
    );
};

/**
 * Use this for images that ALREADY contain a phone (eg the map screenshot has bezel built in).
 * Crops out the surrounding paper texture via overflow-hidden + scale.
 */
export const RawPhoneImage = ({
    src,
    alt = "Voro screen",
    width = 320,
    aspect = "9/19.5",
    scale = 1.55,
    offsetY = 0,
    rotate = 0,
    floatDelay = 0,
    glow = true,
    className = "",
    testId,
}) => {
    return (
        <div className={`relative ${className}`} style={{ width }}>
            {glow && (
                <div
                    className="absolute inset-0 -z-10 blur-3xl opacity-60"
                    style={{
                        background:
                            "radial-gradient(closest-side, rgba(139,92,246,0.55), transparent 70%)",
                    }}
                />
            )}
            <motion.div
                initial={{ y: 0 }}
                animate={{ y: [-12, 12, -12] }}
                transition={{
                    repeat: Infinity,
                    duration: 7 + floatDelay,
                    ease: "easeInOut",
                    delay: floatDelay,
                }}
                style={{
                    transform: `rotate(${rotate}deg)`,
                    aspectRatio: aspect,
                    overflow: "hidden",
                    borderRadius: "2.6rem",
                    boxShadow:
                        "0 30px 80px -20px rgba(139,92,246,0.45), 0 12px 30px -10px rgba(0,0,0,0.6)",
                }}
                className="relative"
            >
                <img
                    data-testid={testId}
                    src={src}
                    alt={alt}
                    draggable={false}
                    className="absolute left-1/2 top-1/2 select-none"
                    style={{
                        transform: `translate(-50%, calc(-50% + ${offsetY}px)) scale(${scale})`,
                        transformOrigin: "center",
                        width: "100%",
                        height: "auto",
                    }}
                    loading="lazy"
                />
                {/* Glass screen sheen */}
                <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-transparent via-white/[0.05] to-transparent" />
            </motion.div>
        </div>
    );
};

export default PhoneMockup;
